<template>
  <div class="template-editor-list h-100 overflow-y-auto d-flex flex-column border-e-sm">
    <div class="flex-shrink-0 pa-4">
      <HideRbac :permissions="['createApprovalTemplate']">
        <v-btn
          variant="flat"
          color="primary"
          size="small"
          rounded="lg"
          class="text-button-3"
          @click="$emit('create')"
        >
          Create New Approval Template
        </v-btn>
      </HideRbac>
    </div>

    <button
      v-for="(template, index) in sortedTemplates"
      :key="template.ulid ?? index"
      type="button"
      class="item cursor-pointer d-flex align-center ga-2 py-2 px-4"
      :class="{
        'bg-indigo100 pl-5 pr-4': isSelected(template),
      }"
      @click="$emit('select', template.ulid)"
    >
      <div class="flex-grow-1">
        <div class="d-flex align-center ga-2">
          <v-sheet
            v-if="template.ulid === null"
            class="bg-success rounded-circle"
            height="8"
            width="8"
          />
          <p
            class="text-body-2 text-left"
            :class="nameColor(template)"
            style="font-weight: 600;"
          >
            {{ template.name }}
          </p>
        </div>
        <p
          class="text-body-3 text-left"
          :class="template.is_archived ? 'text-textLight' : ''"
        >
          {{ template.description }}
        </p>
        <p
          class="text-body-3 text-left"
          :class="template.is_archived ? 'text-textLight' : 'text-textDark'"
        >
          {{ template.stages.length }} stage{{ template.stages.length !== 1 ? 's' : '' }} ·
          {{ slotCount(template) }} slot{{ slotCount(template) !== 1 ? 's' : '' }}
        </p>
      </div>
      <v-icon
        v-if="isSelected(template)"
        icon="$chevron-right"
        size="14"
        color="primary"
      />
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import HideRbac from '@/components/rbac/HideRbac.vue';

defineOptions({ name: 'TemplateEditorList' });

const props = defineProps({
  templates: { type: Array, default: () => [] },
  selectedUlid: { type: [String, null], default: null },
  hasUnsaved: { type: Boolean, default: false },
});

defineEmits(['select', 'create']);

const sortedTemplates = computed(() => [
  ...props.templates.filter((t) => !t.is_archived),
  ...props.templates.filter((t) => t.is_archived),
]);

const isSelected = (template) => {
  if (template.ulid === null) return props.selectedUlid === null && props.hasUnsaved;
  return template.ulid === props.selectedUlid;
};

const nameColor = (template) => {
  if (template.is_archived) return 'text-textLight';
  if (isSelected(template)) return 'text-primary';
  return 'text-textDarkest';
};

const slotCount = (template) => template.stages.reduce(
  (sum, stage) => sum + (stage.slots?.length ?? 0),
  0,
);
</script>

<style lang="less" scoped>
.template-editor-list {
  .item {
    display: block;
    border-left: 3px solid transparent;

    &:hover:not(.bg-indigo100) {
      background: rgb(var(--v-theme-bgDark));
    }

    &.bg-indigo100 {
      border-left-color: @indigo-500;
    }
  }
}
</style>
